// «Разбор ИИ» внизу завершённой тренировки: итог, оценка весов по упражнениям, что дальше.
// Разбор делает задание сервера сам после завершения и хранит; «Разобрать» / «Разобрать заново»
// ставят его в очередь. Пока разбор ждёт, карточка перечитывает его раз в несколько секунд.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Button, Card, ErrorText, Icon, Spinner } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { aiApi, aiKeys, type SessionReview } from '@/pages/ai/api'
import { Disclaimer, Markdown, NotConfigured } from '@/pages/ai/parts'

export const POLL_MS = 5000

export function SessionReviewCard({ sessionId }: { sessionId: string }) {
  const client = useQueryClient()
  const review = useQuery({
    queryKey: aiKeys.sessionReview(sessionId),
    queryFn: () => aiApi.sessionReview(sessionId),
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? POLL_MS : false),
  })
  const request = useMutation({
    mutationFn: () => aiApi.requestSessionReview(sessionId),
    onSuccess: (next: SessionReview) => client.setQueryData(aiKeys.sessionReview(sessionId), next),
  })
  const status = review.data?.status
  const busy = request.isPending || status === 'pending'

  let body
  if (review.isPending) {
    body = <Spinner size={18} />
  } else if (review.error) {
    body = <ErrorText error={review.error} />
  } else if (status === 'none') {
    body = <p className="text-sm text-muted">Оценка выполненного и весов на следующий раз — по кнопке «Разобрать».</p>
  } else if (status === 'pending') {
    body = (
      <p className="flex items-center gap-2 text-sm text-muted">
        <Icon name="info" size={18} />
        Разбор готовится — появится здесь через минуту-две.
      </p>
    )
  } else if (status === 'not_configured') {
    body = <NotConfigured />
  } else if (status === 'error') {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p className="text-sm text-danger" role="alert">
          {review.data?.error ?? 'Не удалось получить разбор.'}
        </p>
        <Button onClick={() => request.mutate()} loading={request.isPending}>
          Повторить
        </Button>
      </div>
    )
  } else {
    body = (
      <div className="flex flex-col gap-2">
        <Markdown text={review.data?.content_md ?? ''} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {review.data?.generated_at ? (
            <span className="text-xs text-muted">Сделан {formatDateTime(review.data.generated_at)}</span>
          ) : null}
          <Disclaimer />
        </div>
      </div>
    )
  }

  return (
    <Card
      title="Разбор ИИ"
      className="mt-4"
      actions={
        status === 'ok' || status === 'none' ? (
          <Button variant="ghost" icon="ai" onClick={() => request.mutate()} loading={busy} disabled={busy}>
            {status === 'ok' ? 'Разобрать заново' : 'Разобрать'}
          </Button>
        ) : null
      }
    >
      {body}
      <ErrorText error={request.error} />
    </Card>
  )
}
